import React, { useRef, useEffect } from "react";
import styles from "./Background.module.scss";
import * as PIXI from "pixi.js";
import { MeshMaterial } from "pixi.js";
import { parallel, calc, listen } from "popmotion";
import multiPhysics from "./multiPhysics";
import { Point, MultiPhysicsSource } from "./multiPhysicsTypes";
import drawVert from "./shaders/draw.vert";
import drawFrag from "./shaders/draw.frag";
import useWindowSize from "./useWindowSize";

const speedPerSecond = calc.speedPerSecond;

PIXI.settings.RESOLUTION = window.devicePixelRatio;

const LINE_RESOLUTION = 30;
const OBJ_RESOLUTION = 10;

const OBJ_RADIUS = 40;
const OBJ_TOP_CENTER = 120;

const MAX_HORIZONTAL_LINE_DIST = 50;
const MAX_HORIZONTAL_OBJ_DIST = OBJ_RADIUS + 40;

type Props = {
  type: string;
  page: string;
};

const generateLine = (horizontalOffset: number): Point[] => {
  const line: Point[] = [];

  const totalHeight = window.innerHeight;

  for (let i = -1; i < totalHeight / LINE_RESOLUTION + 2; i++) {
    line.push([horizontalOffset, i * LINE_RESOLUTION]);
  }

  return line;
};

const generatePlanet = (horizontalOffset: number): Point[] => {
  const line: Point[] = [];
  const max = Math.ceil((OBJ_RADIUS / OBJ_RESOLUTION) * 4);

  // Circle
  let intersection = Math.round((max * 3) / 20);
  for (let i = 0; i <= max; i++) {
    const x =
      Math.cos(((i + intersection) / max) * 2 * Math.PI) * OBJ_RADIUS +
      horizontalOffset;
    const y =
      Math.sin(((i + intersection) / max) * 2 * Math.PI) * OBJ_RADIUS +
      OBJ_TOP_CENTER;
    line.push([x, y]);
  }

  const a = 0.8;
  const b = 1.3;
  const t = Math.PI / 3;

  // Elipse
  intersection = Math.round((max * 1) / 30);
  for (let i = 0; i <= max; i++) {
    const x =
      a * Math.cos(((i - intersection) / max) * 2 * Math.PI) * OBJ_RADIUS +
      horizontalOffset;
    const y =
      b * Math.sin(((i - intersection) / max) * 2 * Math.PI) * OBJ_RADIUS +
      OBJ_TOP_CENTER;

    const cx = horizontalOffset;
    const cy = OBJ_TOP_CENTER;

    // Apply rotation
    line.push([
      x * Math.cos(t) -
        y * Math.sin(t) +
        cx -
        Math.cos(t) * cx +
        Math.sin(t) * cy,
      x * Math.sin(t) -
        y * Math.cos(t) +
        cy -
        Math.sin(t) * cx +
        Math.cos(t) * cy,
    ]);
  }

  return line;
};

const generateCoding = (horizontalOffset: number): Point[] => {
  const line: Point[] = [];

  const scale = OBJ_RADIUS;

  line.push([-1 * scale + horizontalOffset, -1 * scale + OBJ_TOP_CENTER]);
  line.push([-2 * scale + horizontalOffset, 0 * scale + OBJ_TOP_CENTER]);
  line.push([-1 * scale + horizontalOffset, 1 * scale + OBJ_TOP_CENTER]);
  line.push([1 * scale + horizontalOffset, -1 * scale + OBJ_TOP_CENTER]);
  line.push([2 * scale + horizontalOffset, 0 * scale + OBJ_TOP_CENTER]);
  line.push([1 * scale + horizontalOffset, 1 * scale + OBJ_TOP_CENTER]);

  return line;
};

const Background = ({ type, page }: Props) => {
  const refContainer = useRef(null);
  const center = useRef<Point[]>();
  const horizontalOffset = useRef<number>();
  const MAX_HORIZONTAL_DIST = useRef<number>();
  const uniforms = useRef<any>({
    time: 0,
    pointer: [0, 0],
  });
  const app = useRef<PIXI.Application>();
  const w = useWindowSize();

  useEffect(() => {
    if (type === "post") {
      const myElement = document.querySelector(".article-body");
      const bound = myElement.getBoundingClientRect();
      horizontalOffset.current = bound.left + 10;
    } else if (type === "page") {
      const myElement = document.querySelector(".page-article");
      const bound = myElement.getBoundingClientRect();
      horizontalOffset.current = bound.left + bound.width / 2;
    } else {
      const myElement = document.querySelector(".innerSidebar");
      const bound = myElement.getBoundingClientRect();
      horizontalOffset.current = bound.left + bound.width + 10;
    }

    if (type === "page" && page === "/pages/software") {
      center.current = generateCoding(horizontalOffset.current);
      MAX_HORIZONTAL_DIST.current = MAX_HORIZONTAL_OBJ_DIST;
    } else if (type === "page" && page === "/pages/aerospace") {
      center.current = generatePlanet(horizontalOffset.current);
      MAX_HORIZONTAL_DIST.current = MAX_HORIZONTAL_OBJ_DIST;
    } else {
      center.current = generateLine(horizontalOffset.current);
      MAX_HORIZONTAL_DIST.current = MAX_HORIZONTAL_LINE_DIST;
    }
  }, [type, page, w.width, w.height]);

  useEffect(() => {
    app.current = new PIXI.Application({
      view: refContainer.current,
      backgroundColor: 0xffffff,
      resizeTo: window,
      autoDensity: true,
      antialias: true,
    });
  }, []);

  useEffect(() => {
    const geometry = new PIXI.Geometry();

    const shader = PIXI.Shader.from(drawVert, drawFrag, uniforms.current);

    const mesh = new PIXI.Mesh(
      geometry,
      shader as MeshMaterial,
      undefined,
      PIXI.DRAW_MODES.LINE_STRIP
      // PIXI.DRAW_MODES.POINTS
    );

    mesh.position.set(0, 0);

    app.current.stage.addChild(mesh);

    geometry.addAttribute("aVertexPosition", center.current.flat());

    const buffer = geometry.getBuffer("aVertexPosition");
    const data = buffer.data as Float32Array;

    let sources: MultiPhysicsSource[][] = center.current.map(() => []);

    const actions = parallel(
      ...center.current.map((v, i) =>
        multiPhysics({
          from: v,
          friction: 0.1,
          springs: [
            {
              origin: center.current[i],
              strength: 10,
            },
          ],
        })
      )
    ).start((v: number[]) => {
      v.flat().forEach((val: number, i: number) => {
        data[i] = val;
      });
      buffer.update();
      uniforms.current.time += 0.05;
    });

    const onMousemove = listen(document, "mousemove").start((e: MouseEvent) => {
      actions.setSources(
        sources.map(() => {
          let x = e.clientX;
          if (
            e.clientX >
            horizontalOffset.current + MAX_HORIZONTAL_DIST.current
          )
            x = horizontalOffset.current + MAX_HORIZONTAL_DIST.current;
          if (
            e.clientX <
            horizontalOffset.current - MAX_HORIZONTAL_DIST.current
          )
            x = horizontalOffset.current - MAX_HORIZONTAL_DIST.current;

          return [
            {
              origin: [x, e.clientY],
              strength: -600000,
            },
          ];
        })
      );
    });

    const onTouchmove = listen(document, "touchmove").start((e: TouchEvent) => {
      actions.setSources(
        sources.map(() => {
          const s = [];
          for (let i = 0; i < e.touches.length; i++) {
            const t = e.touches[i];

            s.push({
              origin: [t.clientX, t.clientY],
              strength: -600000,
            });
          }

          return s;
        })
      );
    });

    const onTouchend = listen(document, "touchend").start((e: MouseEvent) => {
      actions.setSources(
        sources.map(() => {
          return [];
        })
      );
    });

    let last_known_scroll_position = 0;
    let isScrolling: number;
    const onScroll = listen(document, "scroll").start((e: Event) => {
      // TODO: Properly compute speed per second
      let scrollSpeed = last_known_scroll_position - window.scrollY;
      if (scrollSpeed > 10) {
        scrollSpeed = 10;
      }
      actions.setAcceleration(
        sources.map((v, i) => {
          const a = 200 + scrollSpeed * 2;
          const k = 1 / 50;
          const d = window.scrollY / 15;
          return [a * Math.sin(k * (i * LINE_RESOLUTION + d)), 0];
        })
      );

      if (typeof isScrolling !== "undefined") {
        window.clearTimeout(isScrolling);
      }

      isScrolling = window.setTimeout(function () {
        actions.setAcceleration(
          sources.map(() => {
            return [0, 0];
          })
        );
      }, 66);

      last_known_scroll_position = window.scrollY;
    });

    return () => {
      actions.stop();

      onMousemove.stop();
      onTouchmove.stop();
      onTouchend.stop();
      onScroll.stop();

      app.current.stage.removeChild(mesh);
    };
  }, [type, page, w.width, w.height]);

  return <canvas ref={refContainer} className={styles["background"]} />;
};

export default Background;
